<script lang="ts" setup>
import { useArraySome, useTimeoutPoll } from '@vueuse/core';
import { useToast } from 'vue-toastification';
import { minutesToMilliseconds } from 'date-fns';

import { useFetchSigner } from '../../../../repositories/signer';
import { PixTutorial, PixConfirmation, PixFailed, SkeletonGenericRequirement } from '../../../../components';
import { useRequirementStep } from '../../../../composables/use-requirement-step';
import { useFetchRequirement } from '../../../../repositories/requirement';
import { saveRequirement, createPixCharge, fetchPixCharge } from '../../../../services';
import { useSubSteps } from '../../../../composables/use-sub-steps';
import { PixCharge } from '../../../../schemas/pix';
import { Evidence } from '../../../../schemas/requirement';

const STEP_NAME = 'pix';

const pixCharge = ref<PixCharge>({
  key: '',
  amount: '',
  e2eId: '',
  qrCodeText: '',
  qrCodeImage: '',
});

const enum SubStep {
  Tutorial = 'tutorial',
  Sign = 'sign',
  Failed = 'failed',
}

const { setSubStep, isSubStep } = useSubSteps([
  SubStep.Tutorial,
  SubStep.Sign,
  SubStep.Failed,
]);

const { t } = useI18n();
const toast = useToast();

const title = t('requirement.pix.title');

useHead({ title });

const { finishRequirement, signatureRequestKey, setActiveRequirement } = useRequirementStep();
const { signer } = useFetchSigner(signatureRequestKey);

setActiveRequirement(STEP_NAME);

const { requirement, suspense } = useFetchRequirement(STEP_NAME, signatureRequestKey.value, { staleTime: minutesToMilliseconds(20) });

const hasEvidence = useArraySome(() => requirement.value?.step?.evidences ?? [], (evidence: Evidence) => evidence.state);
const hasSavedEvidence = useArraySome(() => requirement.value?.step?.evidences ?? [], (evidence: Evidence) => evidence.state !== 'failed');
const allEvidencesWasFailed = computed(() => hasEvidence.value && !hasSavedEvidence.value);

const isLoading = ref(true);

async function fetchPixChargeStatus(): Promise<void> {
  try {
    pixCharge.value = await fetchPixCharge(signatureRequestKey.value, pixCharge.value.key);
  } catch (error) {
    toast.error(t('requirement.pix.error.unexpected'));
    throw error;
  }
}

async function fetchEvidence() {
  const { data } = await suspense();

  const key = data?.step.evidences.findLast((e: Evidence) => e.state === 'pending')?.artefact.key;

  pixCharge.value.key = key ?? '';
}

async function saveEvidence() {
  try {
    await suspense();
    await saveRequirement(STEP_NAME, signatureRequestKey.value, { pixChargeKey: pixCharge.value.key });
  } catch (error) {
    toast.error(t('requirement.pix.error.unexpected'));
    throw error;
  }
}

async function initPixCharge() {
  if (!signer.value?.documentation) throw new Error('Signer documentation missing');

  pixCharge.value = await createPixCharge(signatureRequestKey.value, signer.value.documentation);
  await saveEvidence();
}

const { pause, resume } = useTimeoutPoll(fetchPixChargeStatus, 3000);

function failed() {
  pause();
}

function goToSign() {
  setSubStep(SubStep.Sign);
}

async function tryAgain() {
  await initPixCharge();
  resume();
  setSubStep(SubStep.Sign);
}

onMounted(async () => {
  await fetchEvidence();

  if (allEvidencesWasFailed.value) {
    setSubStep(SubStep.Failed);
  } else {
    if (!pixCharge.value.key && !hasEvidence.value) {
      await initPixCharge();
    }
    resume();
  }

  isLoading.value = false;
});

watch(() => pixCharge.value, () => {
  const { status } = pixCharge.value;
  const errorStatus = ['failed', 'refunded', 'refunding', 'canceled'];

  if (status === 'success') {
    pause();
    finishRequirement();
  } else if (status && errorStatus.includes(status)) {
    pause();
    setSubStep(SubStep.Failed);
  }
});
</script>

<template>
  <section
    data-testid="pixSignatureStep"
    :class="$style.pix"
  >
    <template v-if="isLoading">
      <SkeletonGenericRequirement />
    </template>
    <template v-else>
      <PixTutorial
        v-if="isSubStep(SubStep.Tutorial)"
        @finish="goToSign"
      />

      <PixConfirmation
        v-if="isSubStep(SubStep.Sign)"
        :pix-charge="pixCharge"
      />

      <PixFailed
        v-if="isSubStep(SubStep.Failed)"
        @try-again="tryAgain"
        @vue:before-mount="failed"
      />
    </template>
  </section>
</template>

<style lang="scss" module>
.pix {
  display: flex;
  align-items: center;
  flex-direction: column;
  max-width: var(--step-max-width);
  margin: auto;
  padding: var(--space-medium);
  text-align: center;
  color: var(--color-neutral-900);
}
</style>
